import { defineStore } from 'pinia'
import { asyncRouterMap, constantRouterMap } from '@/router'
import { generateRoutesByServer, flatMultiLevelRoutes } from '@/utils/routerHelper'
import { store } from '../index'
import { cloneDeep } from 'lodash-es'
import router from '@/router'
import type { RouteRecordRaw } from 'vue-router'
import { getUserAppCustomRouteApi } from '@/api/admin/user/permission'
import { useTagsViewStoreWithOut } from './tagsView'

export interface PermissionState {
  routers: AppRouteRecordRaw[]
  addRouters: AppRouteRecordRaw[]
  isAddRouters: boolean
  menuTabRouters: AppRouteRecordRaw[]
}

export const usePermissionStore = defineStore('permission', {
  state: (): PermissionState => ({
    routers: [],
    addRouters: [],
    isAddRouters: false,
    menuTabRouters: []
  }),
  getters: {
    getRouters(): AppRouteRecordRaw[] {
      return this.routers
    },
    getAddRouters(): AppRouteRecordRaw[] {
      return flatMultiLevelRoutes(cloneDeep(this.addRouters))
    },
    getIsAddRouters(): boolean {
      return this.isAddRouters
    },
    getMenuTabRouters(): AppRouteRecordRaw[] {
      return this.menuTabRouters
    }
  },
  actions: {
    generateRoutes(
      type: 'server' | 'static',
      routers?: AppCustomRouteRecordRaw[] | string[]
    ): Promise<unknown> {
      return new Promise<void>((resolve) => {
        let routerMap: AppRouteRecordRaw[] = []
        if (type === 'server') {
          // 模拟后端过滤菜单
          routerMap = generateRoutesByServer(routers as AppCustomRouteRecordRaw[])
        } else if (type === 'static') {
          // 直接读取静态路由表
          routerMap = routerMap = cloneDeep(asyncRouterMap)
        }
        // 动态路由，404一定要放到最后面
        this.addRouters = routerMap.concat([
          {
            path: '/:path(.*)*',
            redirect: '/404',
            name: '404Page',
            meta: {
              hidden: true,
              breadcrumb: false
            }
          }
        ])
        // 渲染菜单的所有路由
        this.routers = cloneDeep(constantRouterMap).concat(routerMap)
        resolve()
      })
    },
    async refreshCurrentUserRoutes(): Promise<void> {
      // Snapshot only registered dynamic names; never reset constant/client routes.
      const oldNames = new Set<string>()
      const collect = (routes: AppRouteRecordRaw[]) => {
        for (const route of routes) {
          if (route.name) oldNames.add(String(route.name))
          if (route.children) collect(route.children)
        }
      }
      collect(this.addRouters)
      const currentPath = router.currentRoute.value.fullPath
      const onOldRoute = router.currentRoute.value.matched.some(
        (route) => route.name && oldNames.has(String(route.name))
      )
      // A removed route does not unmount its current view. Leave it before revoking registration.
      if (onOldRoute) await router.replace('/admin/dashboard/welcome')
      // Remove before requesting the new menu: failed requests must not retain revoked access.
      for (const name of oldNames) {
        if (router.hasRoute(name)) router.removeRoute(name)
      }
      this.addRouters = []
      this.routers = cloneDeep(constantRouterMap)
      this.menuTabRouters = []
      this.isAddRouters = false
      useTagsViewStoreWithOut().delAllViews()

      const roleRouters = await getUserAppCustomRouteApi(true)
      await this.generateRoutes('server', roleRouters)
      this.getAddRouters.forEach((route) => router.addRoute(route as RouteRecordRaw))
      this.isAddRouters = true
      if (
        onOldRoute &&
        router
          .resolve(currentPath)
          .matched.some((route) => route.name && !['404Page', '404'].includes(String(route.name)))
      ) {
        await router.replace(currentPath)
      }
    },
    setIsAddRouters(state: boolean): void {
      this.isAddRouters = state
    },
    setMenuTabRouters(routers: AppRouteRecordRaw[]): void {
      this.menuTabRouters = routers
    }
  },
  persist: [
    {
      pick: ['routers'],
      storage: localStorage
    },
    {
      pick: ['addRouters'],
      storage: localStorage
    },
    {
      pick: ['menuTabRouters'],
      storage: localStorage
    }
  ]
})

export const usePermissionStoreWithOut = () => {
  return usePermissionStore(store)
}
